export const CART_QUANTITY_UNITS = {
  LOT: "lot",
  PIECE: "piece",
} as const;

export type CartQuantityUnit = (typeof CART_QUANTITY_UNITS)[keyof typeof CART_QUANTITY_UNITS];

export const normalizeConditioning = (value: unknown) => {
  const conditioning = Number(value);

  return Number.isInteger(conditioning) && conditioning > 0 ? conditioning : 1;
};

// `conditioning` is Vary's per-piece quantity; `conditioning_bac` is the
// number of physical pieces in a bac whose rental price is already complete.
// Search indexes can coerce both fields to numbers, so the field name — not
// the runtime JavaScript type received from Meilisearch — owns the price unit.
export const resolveProductConditioning = (conditioning: unknown, conditioningBac: unknown): number | string => {
  const pieceConditioning = normalizeConditioning(conditioning);

  if (pieceConditioning > 1) return pieceConditioning;

  const bacConditioning = normalizeConditioning(conditioningBac);
  return bacConditioning > 1 ? String(bacConditioning) : 1;
};

// Vary exposes per-piece nConditioning as a number and packaged sPackages as a string.
export const getCartQuantityUnit = (conditioning: unknown): CartQuantityUnit =>
  typeof conditioning === "number" && normalizeConditioning(conditioning) > 1
    ? CART_QUANTITY_UNITS.PIECE
    : CART_QUANTITY_UNITS.LOT;

export const getCartQuantityStep = (conditioning: unknown) =>
  getCartQuantityUnit(conditioning) === CART_QUANTITY_UNITS.PIECE ? normalizeConditioning(conditioning) : 1;

export const toPieceQuantity = (lotQuantity: number, conditioning: unknown) =>
  lotQuantity * getCartQuantityStep(conditioning);

export const toLotPrice = (unitPrice: number, conditioning: unknown) => unitPrice * getCartQuantityStep(conditioning);

export const toPiecePrice = (unitPrice: number, conditioning: unknown) =>
  toLotPrice(unitPrice, conditioning) / normalizeConditioning(conditioning);

export const toLotQuantity = (pieceQuantity: number, conditioning: unknown) =>
  pieceQuantity / getCartQuantityStep(conditioning);

export const toPhysicalPieceQuantity = (cartQuantity: number, conditioning: unknown) =>
  (cartQuantity * normalizeConditioning(conditioning)) / getCartQuantityStep(conditioning);

export const getPieceLabelKey = (pieceQuantity: number) => (pieceQuantity === 1 ? "piece" : "pieces");

export const normalizeCartLineQuantity = (
  quantity: number,
  currentUnit: unknown,
  conditioning: unknown
) => {
  const expectedUnit = getCartQuantityUnit(conditioning);

  if (currentUnit === expectedUnit) return quantity;
  if (currentUnit === CART_QUANTITY_UNITS.PIECE && expectedUnit === CART_QUANTITY_UNITS.LOT) {
    return Math.max(1, Math.ceil(quantity / normalizeConditioning(conditioning)));
  }

  // New unmarked lines already use the configured piece step. Only old lot
  // quantities (which are not aligned to that step) still need conversion.
  if (
    currentUnit == null &&
    expectedUnit === CART_QUANTITY_UNITS.PIECE &&
    quantity % getCartQuantityStep(conditioning) === 0
  ) {
    return quantity;
  }

  return expectedUnit === CART_QUANTITY_UNITS.PIECE ? toPieceQuantity(quantity, conditioning) : quantity;
};

export const normalizePieceQuantity = (pieceQuantity: number, conditioning: unknown) => {
  const step = getCartQuantityStep(conditioning);

  return Math.max(step, Math.ceil(pieceQuantity / step) * step);
};
